import React from "react";
import DashboardWidget from "./DashboardWidget";
import { UserGroupIcon, CheckCircleIcon, XCircleIcon } from "@heroicons/react/solid";
import Spinner from "../loader/Spinner";
import useGet from "../../hooks/read/useGet";

const AnalyticsDashboard = () => {
  const { list: statsQuery } = useGet({
    url: "analytics/dashboard-stats",
  });

  const { list: employeeStatsQuery } = useGet({
    url: "analytics/employee-stats",
  });

  const isLoading = statsQuery.isLoading || employeeStatsQuery.isLoading;
  const stats = statsQuery.data?.data;
  const employeeStats = employeeStatsQuery.data?.data || [];

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div className="w-full flex flex-col space-y-6">
      {/* Top Level Widgets */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <DashboardWidget
          icon={UserGroupIcon}
          label="Total Employees"
          count={stats?.totalEmployees || 0}
          link="/employee-details"
        />
        <DashboardWidget
          icon={CheckCircleIcon}
          label="Present Today"
          count={stats?.presentToday || 0}
          link="/attendance"
        />
        <DashboardWidget
          icon={XCircleIcon}
          label="Absent Today"
          count={stats?.absentToday || 0}
          link="/attendance"
        />
      </div>

      {/* Employee Data Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mt-6 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900">Employee Analytics</h3>
          <p className="text-sm text-gray-500">Average working hours, in-time, and late days per month.</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee Name
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Working Hours
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg In-Time
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Out Time
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Late Days / Month
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {employeeStats.length > 0 ? (
                employeeStats.map((emp) => (
                  <tr key={emp.employee_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {emp.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                      {emp.average_working_hours} hrs
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                      {emp.average_in_time}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                      {emp.average_out_time}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        emp.average_late_days > 2 ? 'bg-red-100 text-red-800' : 
                        emp.average_late_days > 0 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {emp.average_late_days}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                    No employee analytics data available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
